/**
 * DealCardOut -> what the deal panel renders.
 *
 * Bound by the presentation-adapter rule in ./README.md. Reshaping and
 * formatting only; every field traces to one the backend sent.
 *
 * Note what is NOT here. The panel shows no per-call recommendation, because
 * the backend has no trustworthy per-call verdict to give: call_snapshots
 * .recommendation_at_analysis_time holds a canonical value on some rows and
 * free-text extractor prose on others. Filling the gap from the CURRENT
 * recommendation is exactly what the old frontend did (audit D4/V3), so the
 * column is absent until the backend can answer it.
 */
import type { DealCard, DealOutcome, MissingEvidenceKey, Participant, ParticipantReviewState, RiskKey } from '../types'
import type { BoardDeal } from './dealCard'
import { toBoardDeal } from './dealCard'

export interface BlockingFact {
  fact: string
  quote: string | null
  changeEvent: string | null
}

export interface DealDetail extends BoardDeal {
  contactEmail: string | null
  contactTitle: string | null
  participants: Participant[]
  participantReview: ParticipantReviewState
  businessContext: string | null
  dealSummary: string | null
  recommendationReason: string | null
  recommendationCitation: string | null
  mandatoryReview: boolean
  missingEvidence: MissingEvidenceKey[]
  userNotes: string | null
  outcome: DealOutcome
  outcomeAt: string | null
  lostReason: string | null
  latestCallDate: string | null
  blockingFacts: BlockingFact[]
  activeRisks: RiskKey[]
}

/** `blocking_facts` is untyped JSON on the wire; read it defensively, invent nothing. */
function toBlockingFacts(raw: unknown): BlockingFact[] {
  if (!Array.isArray(raw)) return []
  const facts: BlockingFact[] = []
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue
    const record = entry as Record<string, unknown>
    const fact = typeof record.fact === 'string' ? record.fact : ''
    if (!fact) continue
    facts.push({
      fact,
      quote: typeof record.quote === 'string' ? record.quote : null,
      changeEvent: typeof record.change_event === 'string' ? record.change_event : null,
    })
  }
  return facts
}

export function toDealDetail(card: DealCard): DealDetail {
  const state = card.deal_current_state ?? null
  return {
    ...toBoardDeal(card),
    contactEmail: card.contact?.email ?? null,
    contactTitle: card.contact?.title ?? null,
    participants: card.participants ?? [],
    participantReview: card.participant_review,
    businessContext: state?.business_context ?? null,
    dealSummary: state?.deal_summary ?? null,
    recommendationReason: state?.recommendation_reason ?? null,
    recommendationCitation: state?.recommendation_citation ?? null,
    mandatoryReview: state?.mandatory_review ?? false,
    missingEvidence: state?.missing_evidence ?? [],
    userNotes: state?.user_notes ?? null,
    outcome: card.outcome ?? null,
    outcomeAt: card.outcome_at ?? null,
    lostReason: card.lost_reason ?? null,
    latestCallDate: state?.latest_call?.call_date ?? null,
    blockingFacts: toBlockingFacts(state?.blocking_facts),
    activeRisks: toBoardDeal(card).activeRisks,
  }
}
